import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { Button } from 'src/components/Button'
import { TextField } from 'src/components/Fields'
import LoadingSpinner from 'src/components/LoadingSpinner'
import OauthButtons from 'src/components/OauthButtons'
import PasswordInput from 'src/components/PasswordField'
import { SlimLayout } from 'src/components/SlimLayout'
import type { OAuthKinds } from 'src/utils/api'
import {
    signup,
    signupWithPasskey,
    isPasskeySupported,
    OAuthScopes,
    authorize,
    getSiteUrlPrefix,
    track,
} from 'src/utils/api'
import gtag, { isGoogleAnalyticsEnabled } from 'src/utils/gtag'
import { getHomeUrl } from 'src/utils/url'
import WaitlistSignUp from './WaitlistSignUp'

type FormData = {
    firstName: string
    lastName: string
    emailAddress: string
    password: string
    referredBy?: string
    referredByOther?: string
}

const AccountSignUpForm = () => {
    const [registrationPending, setRegistrationPending] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [passkeysAvailable, setPasskeysAvailable] = useState(false)
    const [formData, setFormData] = useState<FormData>({
        firstName: '',
        lastName: '',
        emailAddress: '',
        password: '',
    })

    const [registrationDisabled, setRegistrationDisabled] = useState(true)
    useEffect(() => setPasskeysAvailable(isPasskeySupported()), [])
    useEffect(() => {
        const { firstName, lastName, emailAddress, password } = formData
        const disabled = !firstName || !lastName || !emailAddress || !password || registrationPending
        setRegistrationDisabled(disabled)
    }, [formData, registrationPending])

    const searchParams = useSearchParams()
    const returnTo = searchParams.get('return_to') ?? ''
    const redirectTo = searchParams.get('redirect_to')

    const handleApiError = (err: any) => {
        setError(err instanceof Error ? err.message : 'An unknown error occurred')
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
        }))
    }
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const referredByOther = formData.referredBy === 'other' ? formData.referredByOther?.trim() : undefined
        await track('signupStarted', {
            kind: 'email',
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            referredBy: formData.referredBy?.trim(),
            referredByOther,
        })
        setError(null)
        setRegistrationPending(true)
        try {
            const result = await signup(
                {
                    firstName: formData.firstName.trim(),
                    lastName: formData.lastName.trim(),
                    emailAddress: formData.emailAddress.trim(),
                    password: formData.password.trim(),
                },
                {
                    referredBy: formData.referredBy?.trim(),
                    referredByOther,
                    returnTo: redirectTo ?? returnTo,
                },
            )
            if (isGoogleAnalyticsEnabled()) {
                gtag('event', 'conversion', { send_to: 'AW-11493206263/8vrnCLOV5oIaEPfRsegq' })
                gtag('event', 'sign_up', { method: 'email' })
            }
            // we should be using Next's router.replace() however it does not reliably
            //  include cookies and have not invested enough time in configuring
            //  Next to fit our needs. this could potentially hide bugs but for now is OK.
            window.location.href = result?.redirectUrl ?? '/onboarding'
        } catch (err) {
            handleApiError(err)
        } finally {
            setRegistrationPending(false)
        }
    }

    const handlePasskeySignup = async () => {
        setError(null)
        setRegistrationPending(true)
        await track('signupStarted', {
            kind: 'passkey',
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
        }).catch(() => undefined)
        try {
            const result = await signupWithPasskey(
                {
                    firstName: formData.firstName.trim(),
                    lastName: formData.lastName.trim(),
                    emailAddress: formData.emailAddress.trim(),
                },
                { returnTo: redirectTo ?? returnTo },
            )
            if (isGoogleAnalyticsEnabled()) {
                gtag('event', 'sign_up', { method: 'passkey' })
            }
            window.location.href = result.redirectUrl ?? '/onboarding'
        } catch (err) {
            if (err instanceof Error && err.name !== 'NotAllowedError') {
                handleApiError(err)
            }
        } finally {
            setRegistrationPending(false)
        }
    }

    const createOauthSignin = (kind: OAuthKinds, scopes: string[]) => {
        return async () => {
            setError(null)
            setRegistrationPending(true)
            await track('signupStarted', {
                kind,
            }).catch(err => {
                // eat this error
            })
            try {
                const { redirectUrl } = await authorize(kind, scopes, {
                    returnTo,
                    createAccountIfNotExists: true,
                })
                if (isGoogleAnalyticsEnabled()) {
                    gtag('event', 'conversion', { send_to: 'AW-11493206263/8vrnCLOV5oIaEPfRsegq' })
                    gtag('event', 'sign_up', { method: kind })
                }
                // we should be using Next's router.replace() however it does not reliably
                //  include cookies and have not invested enough time in configuring
                //  Next to fit our needs. this could potentially hide bugs but for now is OK.
                window.location.href = redirectUrl ?? `${getSiteUrlPrefix()}/conversation/new`
            } catch (error) {
                handleApiError(error)
            } finally {
                setRegistrationPending(false)
            }
        }
    }
    return (
        <SlimLayout>
            {/* Home is served by Cloudflare Pages; full page load required. */}
            <a
                className="absolute top-4 left-4 flex flex-row items-center gap-1 h-[18px] cursor-pointer"
                href={getHomeUrl()}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M11 14L5 8L11 2" stroke="#89919D" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-sm text-[#3B4049]">Back</span>
            </a>
            <div className="pt-12 pb-8 w-full">
                <p className="text-xl mb-6 text-neutral-900 font-bold">Create an account</p>
                <p className="text-xs mb-3 text-neutral-900">You can sign up with:</p>
                <OauthButtons
                    signinGitHub={createOauthSignin('github', OAuthScopes.github)}
                    signinGitLab={createOauthSignin('gitlab', OAuthScopes.gitlab)}
                    signinGoogle={createOauthSignin('google', OAuthScopes.google)}
                />
                <div className="flex items-center w-full my-4">
                    <div className="flex-grow h-px bg-gray-300"></div>
                    <div className="px-4 text-xs text-gray-500">Or</div>
                    <div className="flex-grow h-px bg-gray-300"></div>
                </div>
                <form method="POST" onSubmit={handleSubmit} className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                    <TextField
                        label="First name"
                        type="text"
                        placeholder="Enter your first name"
                        autoComplete="given-name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                    />
                    <TextField
                        label="Last name"
                        name="lastName"
                        placeholder="Enter your last name"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        type="text"
                        autoComplete="family-name"
                        required
                    />
                    <TextField
                        className="col-span-full"
                        label="Email"
                        name="emailAddress"
                        placeholder="Enter your email address"
                        type="email"
                        value={formData.emailAddress}
                        onChange={handleInputChange}
                        autoComplete="email"
                        required
                    />
                    {passkeysAvailable && (
                        <div className="col-span-full mt-2">
                            <Button
                                type="button"
                                variant="solid"
                                color="blue"
                                className="w-full bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                                disabled={
                                    registrationPending ||
                                    !formData.firstName ||
                                    !formData.lastName ||
                                    !formData.emailAddress
                                }
                                onClick={handlePasskeySignup}
                            >
                                <LoadingSpinner isLoading={registrationPending}>
                                    <span>Create account with a passkey</span>
                                </LoadingSpinner>
                            </Button>
                            <div className="flex items-center w-full mt-4">
                                <div className="flex-grow h-px bg-gray-300"></div>
                                <div className="px-4 text-xs text-gray-500">Or use a password</div>
                                <div className="flex-grow h-px bg-gray-300"></div>
                            </div>
                        </div>
                    )}
                    <PasswordInput
                        className="col-span-full"
                        label="Password"
                        name="password"
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleInputChange}
                        autoComplete="new-password"
                        minLength={8}
                        required
                    />
                    <div className="col-span-full mt-2">
                        <Button
                            type="submit"
                            variant="solid"
                            color="blue"
                            className="w-full bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                            disabled={registrationDisabled}
                        >
                            <LoadingSpinner isLoading={registrationPending}>
                                <span>Create an account with a password</span>
                            </LoadingSpinner>
                        </Button>
                    </div>
                    <div className="col-span-full">
                        {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                    </div>
                </form>
                <p className="text-center mt-3 text-xs text-neutral-700">
                    Already have an account?{' '}
                    <Link href="/signin" className="font-medium text-primary-600 hover:underline">
                        Sign in
                    </Link>
                </p>
            </div>
        </SlimLayout>
    )
}

const SignUpForm = () => {
    const searchParams = useSearchParams()
    const code = searchParams.get('code')
    return code !== null && code !== '' ? <AccountSignUpForm /> : <WaitlistSignUp />
}

export default SignUpForm
